import { useEffect } from "react";
import type { GalleryImage } from "@/types";

interface LightboxProps {
  image: GalleryImage;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  showNavigation: boolean;
}

export default function Lightbox({
  image,
  onClose,
  onPrev,
  onNext,
  showNavigation,
}: LightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && showNavigation) onPrev();
      if (e.key === "ArrowRight" && showNavigation) onNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [onClose, onPrev, onNext, showNavigation]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 cursor-pointer"
        aria-label="Close lightbox"
      >
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-close-line text-2xl"></i>
        </div>
      </button>

      {/* Navigation Buttons */}
      {showNavigation && (
        <>
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 cursor-pointer"
            aria-label="Previous image"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-left-line text-2xl"></i>
            </div>
          </button>
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 cursor-pointer"
            aria-label="Next image"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-right-line text-2xl"></i>
            </div>
          </button>
        </>
      )}

      {/* Image Container */}
      <div className="max-w-7xl max-h-full flex flex-col lg:flex-row items-center gap-8">
        {/* Image */}
        <div className="flex-1 flex items-center justify-center">
          <img
            src={image.image}
            alt={image.alt}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>

        {/* Image Info */}
        <div className="lg:w-80 text-white space-y-4">
          <div>
            <h2 className="text-2xl font-bold mb-2 font-inter">
              {image.title}
            </h2>
            <p className="text-gray-300 text-sm mb-4">
              {image.category} | {image.year} | {image.location}
            </p>
            <p className="text-gray-200 leading-relaxed">{image.caption}</p>
          </div>
        </div>
      </div>

      {/* Click outside to close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onClose}
      ></div>
    </div>
  );
}
